import { useEffect, useRef } from "react";

interface TurnstileProps {
  siteKey: string;
  onToken: (token: string) => void;
  onError?: (code?: string) => void;
  onExpire?: () => void;
  appearance?: "always" | "execute" | "interaction-only";
  size?: "normal" | "flexible" | "compact" | "invisible";
  theme?: "light" | "dark" | "auto";
  className?: string;
}

export const Turnstile = ({
  siteKey,
  onToken,
  onError,
  onExpire,
  appearance = "interaction-only",
  size,
  theme = "auto",
  className,
}: TurnstileProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);

  onTokenRef.current = onToken;
  onErrorRef.current = onError;
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    let cancelled = false;
    let pollId: number | undefined;

    const mount = () => {
      if (cancelled || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: siteKey,
        appearance,
        size,
        theme,
        callback: (token) => onTokenRef.current(token),
        "error-callback": (code) => onErrorRef.current?.(code),
        "expired-callback": () => onExpireRef.current?.(),
      });
    };

    if (window.turnstile) {
      mount();
    } else {
      pollId = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(pollId);
          mount();
        }
      }, 100);
    }

    return () => {
      cancelled = true;
      if (pollId !== undefined) window.clearInterval(pollId);
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // widget may already be gone
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, appearance, size, theme]);

  return <div ref={containerRef} className={className} />;
};

export const resetTurnstileByContainer = (el: HTMLElement | null) => {
  if (!el || !window.turnstile) return;
  try {
    window.turnstile.reset(el);
  } catch {
    // noop
  }
};
